/**
 * StudentSignup - creating an account for someone who DOES chores
 * ---------------------------------------------------------------
 * Asks for a little more than the poster form: date of birth, school year and
 * an optional bio, because that is what posters look at when deciding.
 *
 * DATE OF BIRTH, NOT AGE - since 17 September 2026. A typed age was wrong a
 * year later; the server works the age out from the date and moves it on by
 * itself (backend/utils/age.js). Posters see the age, never the date.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

import { useAuth } from '../hooks/useAuth';
import LocationFields from '../components/LocationFields';
import { markNotificationPromptDue } from '../components/NotificationPrompt';
import TermsCheckbox from '../components/TermsCheckbox';
import SchoolFields from '../components/SchoolFields';

import { useI18n } from '../hooks/useI18n';
import PasswordInput from '../components/PasswordInput';
import BirthDateFields from '../components/BirthDateFields';
import { ageFromBirthDate } from '../utils/format';
import { loadDraft, saveDraft, clearDraft, STUDENT_SIGNUP_DRAFT } from '../utils/draft';

const CATEGORIES = [
  'Cleaning',
  'Gardening',
  'Babysitting',
  'Pet Care',
  'Tutoring',
  'Shopping & Errands',
  'Moving & Lifting',
  'Tech Help',
  'DIY & Repairs',
  'Other',
];

export default function StudentSignup() {
  const { t } = useI18n();
  const { registerStudent } = useAuth();
  const navigate = useNavigate();

  /**
   * WHAT WAS TYPED BEFORE GOING OFF TO READ THE TERMS.
   *
   * Restored from a draft if there is one - see utils/draft.js for where it
   * is kept and why. The default goes inside a function so the lookup
   * happens once when the screen opens rather than on every keystroke.
   */
  const draft = loadDraft(STUDENT_SIGNUP_DRAFT);

  const [form, setForm] = useState(() => ({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    school: '',
    dateOfBirth: '',
    schoolYear: 'S5',
    bio: '',
    guardianName: '',
    guardianEmail: '',
    schoolCode: '',
    ...(draft?.form || {}),
  }));
  const [skills, setSkills] = useState(() => draft?.skills || []);
  const [location, setLocation] = useState(
    () => draft?.location || { city: '', address: '', lat: null, lng: null }
  );
  /**
   * The tickbox is deliberately NOT restored. Everything else here is work
   * the person did; this one is a statement that they have read something,
   * and the site should not remember somebody having said that on their
   * behalf. It is one tap, and it is the tap the record rests on.
   */
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  // Keep the draft in step with the form. Cheap, and it means the work
  // survives any way of leaving the page - a link to the terms, the browser's
  // own back button, or an accidental reload.
  useEffect(() => {
    saveDraft(STUDENT_SIGNUP_DRAFT, { form, skills, location });
  }, [form, skills, location]);

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const toggleSkill = (skill) => {
    setSkills((current) =>
      current.includes(skill) ? current.filter((s) => s !== skill) : [...current, skill]
    );
  };

  const errorFor = (field) => fieldErrors.find((e) => e.field === field)?.message;

  // Under-18s must give us a parent's details. Shown as soon as the date of
  // birth is complete, so the requirement is never a surprise at the end of
  // the form. The server decides for real; this only decides what to show.
  const age = ageFromBirthDate(form.dateOfBirth);
  const needsGuardian = age !== null && age < 18;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setFieldErrors([]);
    setBusy(true);

    try {
      // A draft saved before 17 September may still carry a typed `age`.
      // The server ignores it, and it is dropped here so nobody reading the
      // request wonders which of the two counted.
      const fields = { ...form };
      delete fields.age;
      await registerStudent({ ...fields, skills, location, acceptedTerms });
      // The account exists, so the half-finished copy of it - password and
      // all - has no reason to stay in the tab.
      clearDraft(STUDENT_SIGNUP_DRAFT);
      // Ask about notifications once they land. Not here: the browser only
      // honours a permission request that came from a click, and firing one
      // mid-signup would be a box with no explanation attached - which people
      // refuse, permanently.
      markNotificationPromptDue();
      navigate('/marketplace', { replace: true });
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.fieldErrors || []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="page-header text-center">
        <h1>{t('studentSignup.title')}</h1>
        <p>{t('studentSignup.subtitle')}</p>
      </div>

      <div className="card">
        <form className="form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <div>
                {error}
                {fieldErrors.length > 0 && (
                  <ul>
                    {fieldErrors.map((e) => (
                      <li key={e.field}>{e.message}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Full width, to match the profile page you land on afterwards. */}
          <div className="field">
            <label htmlFor="firstName">{t('form.firstName')} *</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={form.firstName}
              onChange={handleChange}
              autoComplete="given-name"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="lastName">{t('form.lastName')} *</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={form.lastName}
              onChange={handleChange}
              autoComplete="family-name"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="email">{t('form.email')} *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              aria-invalid={Boolean(errorFor('email'))}
              required
            />
            {errorFor('email') && <span className="field-error">{errorFor('email')}</span>}
          </div>

          <div className="field">
            <label htmlFor="password">{t('form.password')} *</label>
            <PasswordInput
              id="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              aria-invalid={Boolean(errorFor('password'))}
              required
            />
            <span className="hint">{t('form.passwordRule')}</span>
            {errorFor('password') && <span className="field-error">{errorFor('password')}</span>}
          </div>

          <div className="form-row">
            <BirthDateFields
              value={form.dateOfBirth}
              onChange={(dateOfBirth) => setForm((current) => ({ ...current, dateOfBirth }))}
              legend={`${t('studentSignup.dateOfBirth')} *`}
              hint={
                age !== null && age >= 0
                  ? t('studentSignup.dateOfBirthAge', { age })
                  : t('studentSignup.dateOfBirthHint')
              }
              error={errorFor('dateOfBirth')}
            />

            <div className="field">
              <label htmlFor="schoolYear">{t('studentSignup.schoolYear')} *</label>
              <select
                id="schoolYear"
                name="schoolYear"
                value={form.schoolYear}
                onChange={handleChange}
                required
              >
                <option value="S5">S5</option>
                <option value="S6">S6</option>
                <option value="S7">S7</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label htmlFor="phone">{t('form.phone')}</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              autoComplete="tel"
              placeholder={t('form.optional')}
            />
          </div>

          <SchoolFields
            school={form.school}
            schoolCode={form.schoolCode}
            onSchoolChange={(v) => setForm((f) => ({ ...f, school: v }))}
            onCodeChange={(v) => setForm((f) => ({ ...f, schoolCode: v }))}
            requireCode
            schoolError={errorFor('school')}
            codeError={errorFor('schoolCode')}
          />

          <div className="field">
            <label htmlFor="bio">{t('studentSignup.about')}</label>
            <textarea
              id="bio"
              name="bio"
              value={form.bio}
              onChange={handleChange}
              maxLength={500}
              placeholder={t('studentSignup.aboutPlaceholder')}
            />
            <span className="hint">{form.bio.length}/500</span>
          </div>

          <div className="field">
            <span className="label">{t('studentSignup.skills')}</span>
            <div className="row" style={{ gap: 8 }}>
              {CATEGORIES.map((category) => {
                const selected = skills.includes(category);
                return (
                  <button
                    key={category}
                    type="button"
                    className={`badge ${selected ? 'badge-green' : 'badge-grey'}`}
                    onClick={() => toggleSkill(category)}
                    aria-pressed={selected}
                    style={{ cursor: 'pointer', border: 'none' }}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
            <span className="hint">{t('studentSignup.skillsHint')}</span>
          </div>

          <LocationFields value={location} onChange={setLocation} />

          {/* ---- Parental consent, only for under-18s ---- */}
          {needsGuardian && (
            <>
              <hr className="divider" />

              <div className="alert alert-info">
                <ShieldCheck size={20} aria-hidden="true" />
                <div>
                  <strong>{t('studentSignup.guardianTitle')}</strong>
                  <br />
                  <span className="text-sm">
                    {t('studentSignup.guardianBody')}
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="field">
                  <label htmlFor="guardianName">{t('studentSignup.guardianName')} *</label>
                  <input
                    id="guardianName"
                    name="guardianName"
                    type="text"
                    value={form.guardianName}
                    onChange={handleChange}
                    aria-invalid={Boolean(errorFor('guardianName'))}
                    required
                  />
                  {errorFor('guardianName') && (
                    <span className="field-error">{errorFor('guardianName')}</span>
                  )}
                </div>

                <div className="field">
                  <label htmlFor="guardianEmail">{t('studentSignup.guardianEmail')} *</label>
                  <input
                    id="guardianEmail"
                    name="guardianEmail"
                    type="email"
                    value={form.guardianEmail}
                    onChange={handleChange}
                    aria-invalid={Boolean(errorFor('guardianEmail'))}
                    required
                  />
                  <span className="hint">{t('studentSignup.guardianEmailHint')}</span>
                  {errorFor('guardianEmail') && (
                    <span className="field-error">{errorFor('guardianEmail')}</span>
                  )}
                </div>
              </div>
            </>
          )}

          <hr className="divider" />

          <TermsCheckbox
            checked={acceptedTerms}
            onChange={setAcceptedTerms}
            error={errorFor('acceptedTerms')}
          />

          <button
            type="submit"
            className="btn btn-secondary btn-block"
            disabled={busy || !acceptedTerms}
          >
            {busy ? 'Creating your account...' : 'Create student account'}
          </button>
        </form>

        <hr className="divider" />

        <p className="text-center text-sm text-muted">
          {t('studentSignup.needChores')} <Link to="/signup/user">{t('studentSignup.signUpHere')}</Link>
          <br />
          {t('userSignup.registered')} <Link to="/login">{t('nav.logIn')}</Link>
        </p>
      </div>
    </div>
  );
}
